import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase.js';
import { listWorkVisits } from '../visits.js';
import { listFiles, signedLinks, uploadFile } from '../attachments/files.js';
import { maxReportDetails, reportContent, reportReasons } from '../safety.js';
import {
  acceptCompletion, cancelWork, getWorkCompletion, markVisitCompleted, maxEvidenceFiles, noteMaxLength, raiseWorkProblem,
  requestFollowUp, respondToReview, reviewMaxLength, reviewMinLength, startWork, submitCompletion, submitReview, workStatusLabel,
} from './completion.js';

const day = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const otherSide = (side) => (side === 'supplier' ? 'the customer' : 'the supplier');
const latest = (events, action) => [...events].reverse().find((event) => event.action === action);
const stars = (n) => '★'.repeat(n) + '☆'.repeat(5 - n);

function eventText(event) {
  const who = event.by_you ? 'You' : event.actor_side === 'supplier' ? 'The supplier' : 'The customer';
  return {
    start: `${who} started the work`, submit: `${who} marked the work as complete`, accept: `${who} accepted the work`,
    follow_up: `${who} asked for a follow-up`, problem: `${who} reported a problem`, cancel: `${who} cancelled the work`,
    cancel_requested: `${who} asked to cancel`, visit_completed: `${who} marked a visit as done`,
  }[event.action] || `${who} updated the work`;
}

function steps(state) {
  const s = state.status; const started = s !== 'awarded';
  return [
    ['Supplier chosen', 'done', ''],
    ['Work started', started ? 'done' : 'current', day(state.started_at)],
    ['Marked as complete', s === 'completed' || s === 'completion_submitted' ? 'done' : started ? 'current' : 'todo',
      s === 'in_progress' && state.follow_up_count ? 'Follow-up requested' : day(state.completion_submitted_at)],
    ['Accepted by customer', s === 'completed' ? 'done' : s === 'completion_submitted' ? 'current' : 'todo', day(state.completed_at)],
  ];
}

function Evidence({ workId, ids }) {
  const [links, setLinks] = useState([]);
  const key = ids.join(',');
  useEffect(() => {
    if (!key || !supabase) return undefined;
    let active = true;
    listFiles(supabase, 'work_record', workId).then(async (rows) => {
      const chosen = rows.filter((row) => key.split(',').includes(row.id));
      const urls = await signedLinks(supabase, chosen);
      if (active) setLinks(chosen.map((row, index) => ({ id: row.id, url: urls[row.storage_path], label: row.caption || (row.mime_type === 'application/pdf' ? `Document ${index + 1}` : `Photo ${index + 1}`) })));
    }).catch(() => undefined);
    return () => { active = false; };
  }, [workId, key]);
  if (!ids.length) return null;
  if (!links.length) return <p><small>{`${ids.length} ${ids.length === 1 ? 'file' : 'files'} attached.`}</small></p>;
  return <ul>{links.map((link) => <li key={link.id}>{link.url ? <a href={link.url} target="_blank" rel="noreferrer">{link.label}</a> : link.label}</li>)}</ul>;
}

/** Report link for a review or response. */
function ReportLink({ targetType, targetId, label }) {
  const [open, setOpen] = useState(false); const [reason, setReason] = useState(''); const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false); const [notice, setNotice] = useState(''); const [error, setError] = useState('');
  async function send(event) {
    event.preventDefault(); setBusy(true); setError('');
    try { await reportContent(supabase, { targetType, targetId, reason, details }); setOpen(false); setNotice('Report sent. We review reports within 24 hours.'); }
    catch (cause) { setError(cause.message); } finally { setBusy(false); }
  }
  return <div className="coord-safety">
    <button type="button" className="text-link" onClick={() => setOpen(!open)} aria-expanded={open}>{label}</button>
    {open && <form className="coord-form" onSubmit={send}>
      <label>Reason<select required value={reason} onChange={(e) => setReason(e.target.value)}><option value="">Choose a reason</option>{reportReasons.map(([value, text]) => <option key={value} value={value}>{text}</option>)}</select></label>
      <label>Details (optional)<textarea rows="3" maxLength={maxReportDetails} value={details} onChange={(e) => setDetails(e.target.value)}/></label>
      <button className="button button-secondary" disabled={busy || !reason}>{busy ? 'Sending…' : 'Send report'}</button>
    </form>}
    {notice && <p className="account-save-status" role="status">{notice}</p>}
    {error && <p className="account-form-error" role="alert">{error}</p>}
  </div>;
}

function ReasonForm({ title, label, hint, confirmLabel, onSubmit, onClose }) {
  const [text, setText] = useState(''); const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  async function submit(event) {
    event.preventDefault(); setBusy(true); setError('');
    try { await onSubmit(text); } catch (cause) { setError(cause.message); setBusy(false); }
  }
  return <form className="coord-form" onSubmit={submit}>
    <h3>{title}</h3>{hint && <p>{hint}</p>}
    <label>{label}<textarea rows="3" required maxLength={noteMaxLength} value={text} disabled={busy} onChange={(e) => setText(e.target.value)}/></label>
    {error && <p className="account-form-error" role="alert">{error}</p>}
    <div className="coord-inline-actions">
      <button className="button button-blue" disabled={busy || !text.trim()}>{confirmLabel}</button>
      <button type="button" className="button button-secondary" disabled={busy} onClick={onClose}>Not now</button>
    </div>
  </form>;
}

function SubmitForm({ workId, onDone, onClose }) {
  const [note, setNote] = useState(''); const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false); const [progress, setProgress] = useState(''); const [error, setError] = useState('');
  async function submit(event) {
    event.preventDefault(); setBusy(true); setError('');
    try {
      const ids = [];
      for (const [index, file] of files.entries()) {
        setProgress(`Uploading ${index + 1} of ${files.length}…`);
        const done = await uploadFile(supabase, { scope: 'work_record', scopeId: workId, file });
        if (done?.status === 'ready') ids.push(done.id);
      }
      setProgress('');
      onDone(await submitCompletion(supabase, workId, note, ids));
    } catch (cause) { setError(cause.message); setBusy(false); setProgress(''); }
  }
  return <form className="coord-form" onSubmit={submit}>
    <h3>Mark as complete</h3>
    <p>The customer will be asked to check the work and accept it or ask for a follow-up.</p>
    <label>What was done (optional)<textarea rows="3" maxLength={noteMaxLength} value={note} disabled={busy} onChange={(e) => setNote(e.target.value)}/></label>
    <label>Photos or certificates (optional)<input type="file" multiple accept="image/jpeg,image/png,image/heic,image/heif,image/webp,application/pdf" disabled={busy}
      onChange={(e) => setFiles([...e.target.files].slice(0, maxEvidenceFiles))}/></label>
    <p><small>{`Up to ${maxEvidenceFiles} files. Files still being checked are not attached.`}</small></p>
    {progress && <p role="status">{progress}</p>}
    {error && <p className="account-form-error" role="alert">{error}</p>}
    <div className="coord-inline-actions">
      <button className="button button-blue" disabled={busy}>Send to customer</button>
      <button type="button" className="button button-secondary" disabled={busy} onClick={onClose}>Not now</button>
    </div>
  </form>;
}

function ReviewForm({ workId, initial, onDone, onClose }) {
  const [rating, setRating] = useState(initial?.rating || 0); const [body, setBody] = useState(initial?.body || '');
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  async function submit(event) {
    event.preventDefault(); setBusy(true); setError('');
    try { await submitReview(supabase, workId, rating, body); onDone(); } catch (cause) { setError(cause.message); setBusy(false); }
  }
  return <form className="coord-form" onSubmit={submit}>
    <h3>{initial ? 'Edit your review' : 'Review this company'}</h3>
    <p>{initial ? 'You can change your review once, within 7 days of posting it.' : 'Your review is public. It shows as from a verified customer, without your name.'}</p>
    <fieldset><legend>Rating</legend>{[1, 2, 3, 4, 5].map((n) => <label key={n}>
      <input type="radio" name="rating" value={n} checked={rating === n} disabled={busy} onChange={() => setRating(n)}/>{` ${n} ${n === 1 ? 'star' : 'stars'}`}</label>)}</fieldset>
    <label>Your review<textarea rows="4" minLength={reviewMinLength} maxLength={reviewMaxLength} required value={body} disabled={busy} onChange={(e) => setBody(e.target.value)}/></label>
    {error && <p className="account-form-error" role="alert">{error}</p>}
    <div className="coord-inline-actions">
      <button className="button button-blue" disabled={busy || rating < 1 || body.trim().length < reviewMinLength}>{initial ? 'Save changes' : 'Post review'}</button>
      <button type="button" className="button button-secondary" disabled={busy} onClick={onClose}>Not now</button>
    </div>
  </form>;
}

function ReviewPanel({ state, form, setForm, reload }) {
  const { review, response, can_review: canReview, can_edit: canEdit, can_respond: canRespond } = state.review;
  if (!review && !canReview) return null;
  return <div>
    <h3>Review</h3>
    {!review && canReview && form !== 'review' && <p>How did it go? Your review helps other customers choose a company. <button className="button button-secondary" onClick={() => setForm('review')}>Write a review</button></p>}
    {form === 'review' && <ReviewForm workId={state.work_id} initial={review?.body ? { rating: review.rating, body: review.body } : null}
      onDone={() => { setForm('none'); reload(); }} onClose={() => setForm('none')}/>}
    {review && form !== 'review' && <article>
      <p aria-label={`${review.rating} out of 5 stars`}><strong>{stars(review.rating)}</strong> <small>{`${review.by_you ? 'Your review' : review.reviewer_label} · ${day(review.created_at)}${review.edited_at ? ' · edited' : ''}`}</small></p>
      {review.hidden && <p>This review has been hidden by Resident Service 360 and is not shown publicly.</p>}
      {review.body && <p style={{ whiteSpace: 'pre-wrap' }}>{review.body}</p>}
      {response && <blockquote><strong>Response from the company</strong>{response.hidden && <p>This response has been hidden by Resident Service 360.</p>}{response.body && <p style={{ whiteSpace: 'pre-wrap' }}>{response.body}</p>}</blockquote>}
      <div className="coord-inline-actions">
        {canEdit && <button className="button button-secondary" onClick={() => setForm('review')}>Edit review</button>}
        {canRespond && form !== 'respond' && <button className="button button-secondary" onClick={() => setForm('respond')}>{response ? 'Edit response' : 'Respond publicly'}</button>}
      </div>
      {!review.by_you && !review.hidden && <ReportLink targetType="review" targetId={review.id} label="Report review"/>}
      {response && !response.hidden && state.my_side === 'customer' && <ReportLink targetType="review_response" targetId={response.id} label="Report response"/>}
    </article>}
    {form === 'respond' && review && <ReasonForm title="Respond publicly" label="Your response" hint="Shown under the review on your company profile."
      confirmLabel="Post response" onClose={() => setForm('none')}
      onSubmit={async (text) => { await respondToReview(supabase, review.id, text); setForm('none'); reload(); }}/>}
  </div>;
}

/** Work detail section: progress, completion with evidence, customer acceptance or follow-up, then the review. Props: { work, onChanged }. */
export function WorkCompletion({ work, onChanged }) {
  const [state, setState] = useState(null); const [visits, setVisits] = useState([]);
  const [error, setError] = useState(''); const [busy, setBusy] = useState(''); const [form, setForm] = useState('none');
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const load = useCallback(async () => {
    if (!supabase || !work?.id) return;
    try {
      const next = await getWorkCompletion(supabase, work.id);
      const all = await listWorkVisits(supabase, work.id).catch(() => []);
      if (!mounted.current) return;
      setState(next); setError('');
      setVisits(all.filter((v) => v.status === 'confirmed' && v.confirmed_end && Date.parse(v.confirmed_end) <= Date.now()));
    } catch (cause) { if (mounted.current) setError(cause.message); }
  }, [work?.id]);
  useEffect(() => { void Promise.resolve().then(load); }, [load]);

  const applied = (next) => { setState(next); setForm('none'); onChanged?.(); };
  async function run(key, action, returnsState = true) {
    setBusy(key); setError('');
    try { const result = await action(); if (returnsState) applied(result); else { setForm('none'); await load(); } }
    catch (cause) { if (mounted.current) setError(cause.message); }
    finally { if (mounted.current) setBusy(''); }
  }

  if (!work?.id) return null;
  if (!state) return <section className="account-content-panel"><h2>Progress</h2>{error ? <div role="alert"><p>{error}</p><button className="button button-secondary" onClick={() => void load()}>Retry</button></div> : <p>Loading progress…</p>}</section>;

  const side = state.my_side;
  const lastSubmit = latest(state.events, 'submit'); const lastFollowUp = latest(state.events, 'follow_up');
  const lastProblem = latest(state.events, 'problem'); const cancelled = latest(state.events, 'cancel');
  const request = state.cancel_request; const agreeing = !!request && !request.by_you;
  const cancelLabel = agreeing ? 'Agree to cancel' : state.cancel_needs_both_sides ? 'Ask to cancel' : 'Cancel work';

  return <section className="account-content-panel">
    <h2>Progress <small>· {workStatusLabel(state.status)}</small></h2>
    {state.status === 'cancelled'
      ? <p>{`This work was cancelled${state.cancelled_at ? ` on ${day(state.cancelled_at)}` : ''}.${cancelled?.note ? ` Reason: ${cancelled.note}` : ''}`}</p>
      : <ol>{steps(state).map(([title, stepState, when]) => <li key={title}>{stepState === 'done' ? '✓ ' : stepState === 'current' ? '→ ' : ''}{title}{when ? ` · ${when}` : ''}</li>)}</ol>}
    {state.blocked && <p role="note">This work is paused because one of you has blocked the other. Contact Resident Service 360 support if you need help settling it.</p>}
    {state.status === 'disputed' && <p role="note">{`A problem has been reported${lastProblem?.note ? `: ${lastProblem.note}` : '.'} Try to sort it out together in messages. If you cannot, contact Resident Service 360 support.`}</p>}
    {state.status === 'in_progress' && lastFollowUp?.note && side === 'supplier' && <p role="note">{`The customer asked for a follow-up: ${lastFollowUp.note}`}</p>}
    {lastSubmit && ['completion_submitted', 'completed', 'disputed'].includes(state.status) && <div>
      <h3>Completion details</h3><p><small>{day(lastSubmit.created_at)}</small></p>
      {lastSubmit.note && <p style={{ whiteSpace: 'pre-wrap' }}>{lastSubmit.note}</p>}
      <Evidence workId={work.id} ids={lastSubmit.attachment_ids || []}/>
    </div>}
    {state.status === 'completion_submitted' && side === 'customer' && <p>Check the work, then accept it or ask the supplier to come back. It stays open until you answer.</p>}
    {state.status === 'completion_submitted' && side === 'supplier' && <p>Waiting for the customer to check and accept the work.</p>}
    {request && state.status !== 'cancelled' && <p role="note">{request.by_you
      ? `You asked to cancel. ${side === 'customer' ? 'The supplier' : 'The customer'} needs to agree before the work is cancelled.`
      : `${request.by_side === 'customer' ? 'The customer' : 'The supplier'} has asked to cancel${request.reason ? `: ${request.reason}` : '.'}`}</p>}

    {form === 'none' && <div className="coord-inline-actions">
      {state.can.start && <button className="button button-blue" disabled={!!busy} onClick={() => void run('start', () => startWork(supabase, work.id))}>Start work</button>}
      {state.can.submit && <button className="button button-blue" disabled={!!busy} onClick={() => setForm('submit')}>Mark as complete</button>}
      {state.can.accept && <button className="button button-blue" disabled={!!busy} onClick={() => { if (window.confirm('Accept the finished work? This confirms the job is done. You can then leave a review.')) void run('accept', () => acceptCompletion(supabase, work.id)); }}>Accept work</button>}
      {state.can.follow_up && <button className="button button-secondary" disabled={!!busy} onClick={() => setForm('follow_up')}>Request a follow-up</button>}
      {state.status !== 'cancelled' && !state.blocked && visits.map((visit) => <button key={visit.id} className="button button-secondary" disabled={!!busy}
        onClick={() => void run(visit.id, () => markVisitCompleted(supabase, visit.id), false)}>{`Mark the ${day(visit.confirmed_start)} visit as done`}</button>)}
      {state.can.problem && <button className="button button-secondary" disabled={!!busy} onClick={() => setForm('problem')}>Report a problem</button>}
      {state.can.cancel && <button className="button button-secondary" disabled={!!busy} onClick={() => setForm('cancel')}>{cancelLabel}</button>}
    </div>}
    {form === 'submit' && <SubmitForm workId={work.id} onDone={applied} onClose={() => setForm('none')}/>}
    {form === 'follow_up' && <ReasonForm title="Request a follow-up" label="What still needs doing?" hint="The supplier will be asked to come back and finish."
      confirmLabel="Send request" onClose={() => setForm('none')} onSubmit={async (text) => applied(await requestFollowUp(supabase, work.id, text))}/>}
    {form === 'problem' && <ReasonForm title="Report a problem" label="What is wrong?" hint={`We let ${otherSide(side)} know. If you cannot sort it out together, Resident Service 360 support can help.`}
      confirmLabel="Report problem" onClose={() => setForm('none')} onSubmit={async (text) => applied(await raiseWorkProblem(supabase, work.id, text))}/>}
    {form === 'cancel' && <ReasonForm title={cancelLabel} label="Reason"
      confirmLabel={agreeing ? 'Agree and cancel' : state.cancel_needs_both_sides ? 'Send request' : 'Cancel work'}
      hint={agreeing ? 'The work will be cancelled for both of you.' : state.cancel_needs_both_sides
        ? `Work has started or a payment is in progress, so ${otherSide(side)} needs to agree. Payments are not refunded automatically.`
        : 'Work has not started, so this cancels it straight away.'}
      onClose={() => setForm('none')} onSubmit={async (text) => applied(await cancelWork(supabase, work.id, text))}/>}
    {error && <p className="account-form-error" role="alert">{error}</p>}

    <ReviewPanel state={state} form={form} setForm={setForm} reload={() => void load()}/>

    {state.events.length > 0 && <details><summary>History</summary><ul>{state.events.map((event) => <li key={event.id}>
      {`${day(event.created_at)} · ${eventText(event)}${event.note && event.action !== 'submit' ? `: ${event.note}` : ''}`}</li>)}</ul></details>}
  </section>;
}
